import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { KpiCard } from './KpiCard';
import {
  ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, CartesianGrid, Legend,
  PieChart, Pie, Cell, AreaChart, Area,
} from 'recharts';
import { BookOpen, CheckCircle2, Users, Library } from 'lucide-react';
import type { UsageMetrics } from '@/hooks/useExecutiveUsageMetrics';
import { formatBucketLabel, Granularity } from '@/lib/timeSeries';

const CHART_COLORS = ['hsl(var(--primary))', 'hsl(var(--secondary))', 'hsl(var(--accent))', 'hsl(var(--info))', 'hsl(var(--success))', 'hsl(var(--warning))'];

interface Props {
  data?: UsageMetrics;
  isLoading: boolean;
  granularity: Granularity;
}

export function ContentTab({ data, isLoading, granularity }: Props) {
  if (isLoading || !data) {
    return <div className="space-y-4"><Skeleton className="h-32" /><Skeleton className="h-72" /></div>;
  }

  const c = data.content;
  const series = c.series.map(s => ({ label: formatBucketLabel(s.bucket, granularity), completados: s.completados }));
  const completionRate = c.totals.vistas > 0 ? Math.round((c.totals.completados / c.totals.vistas) * 100) : 0;

  return (
    <div className="space-y-6">
      <p className="text-xs text-muted-foreground">
        Los indicadores y tablas de consumo de contenido son históricos (acumulados desde el inicio):
        el registro de consumo no guarda fecha de apertura. Solo la gráfica de “completados” respeta el rango seleccionado.
      </p>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <KpiCard label="Contenidos abiertos (histórico)" value={c.totals.vistas.toLocaleString('es-CO')} icon={<BookOpen className="w-4 h-4" />} accent="primary" />
        <KpiCard label="Contenidos completados (histórico)" value={c.totals.completados.toLocaleString('es-CO')} hint={`${completionRate}% de finalización`} icon={<CheckCircle2 className="w-4 h-4" />} accent="success" />
        <KpiCard label="Usuarios consumiendo (histórico)" value={c.totals.usuarios.toLocaleString('es-CO')} icon={<Users className="w-4 h-4" />} accent="info" />
        <KpiCard label="Recursos distintos vistos (histórico)" value={c.totals.contenidos.toLocaleString('es-CO')} icon={<Library className="w-4 h-4" />} accent="accent" />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card className="glass-card">
          <CardHeader><CardTitle className="text-base">Consumo por categoría</CardTitle></CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={320}>
              <BarChart data={c.categories.slice(0, 12)} layout="vertical" margin={{ left: 40 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                <XAxis type="number" fontSize={11} stroke="hsl(var(--muted-foreground))" />
                <YAxis type="category" dataKey="name" width={150} fontSize={11} stroke="hsl(var(--muted-foreground))" />
                <Tooltip contentStyle={{ background: 'hsl(var(--card))', border: '1px solid hsl(var(--border))', borderRadius: 8 }} />
                <Legend />
                <Bar dataKey="vistas" name="Abiertos" fill="hsl(var(--primary))" radius={[0,4,4,0]} />
                <Bar dataKey="completados" name="Completados" fill="hsl(var(--success))" radius={[0,4,4,0]} />
              </BarChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>

        <Card className="glass-card">
          <CardHeader><CardTitle className="text-base">Formato y nivel de acceso</CardTitle></CardHeader>
          <CardContent className="grid grid-cols-2 gap-2">
            <ResponsiveContainer width="100%" height={300}>
              <PieChart>
                <Pie data={c.types} dataKey="vistas" nameKey="name" outerRadius={80} label>
                  {c.types.map((_, i) => <Cell key={i} fill={CHART_COLORS[i % CHART_COLORS.length]} />)}
                </Pie>
                <Tooltip contentStyle={{ background: 'hsl(var(--card))', border: '1px solid hsl(var(--border))', borderRadius: 8 }} />
                <Legend />
              </PieChart>
            </ResponsiveContainer>
            <ResponsiveContainer width="100%" height={300}>
              <PieChart>
                <Pie data={c.access} dataKey="vistas" nameKey="name" outerRadius={80} label>
                  {c.access.map((_, i) => <Cell key={i} fill={CHART_COLORS[(i + 2) % CHART_COLORS.length]} />)}
                </Pie>
                <Tooltip contentStyle={{ background: 'hsl(var(--card))', border: '1px solid hsl(var(--border))', borderRadius: 8 }} />
                <Legend />
              </PieChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>
      </div>

      <Card className="glass-card">
        <CardHeader><CardTitle className="text-base">Contenidos completados en el tiempo</CardTitle></CardHeader>
        <CardContent>
          <ResponsiveContainer width="100%" height={260}>
            <AreaChart data={series}>
              <defs>
                <linearGradient id="grContent" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="hsl(var(--accent))" stopOpacity={0.4} />
                  <stop offset="100%" stopColor="hsl(var(--accent))" stopOpacity={0} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
              <XAxis dataKey="label" fontSize={11} stroke="hsl(var(--muted-foreground))" />
              <YAxis fontSize={11} stroke="hsl(var(--muted-foreground))" />
              <Tooltip contentStyle={{ background: 'hsl(var(--card))', border: '1px solid hsl(var(--border))', borderRadius: 8 }} />
              <Area type="monotone" dataKey="completados" name="Completados" stroke="hsl(var(--accent))" fill="url(#grContent)" strokeWidth={2} />
            </AreaChart>
          </ResponsiveContainer>
        </CardContent>
      </Card>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card className="glass-card">
          <CardHeader><CardTitle className="text-base">Top contenidos más vistos</CardTitle></CardHeader>
          <CardContent>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Contenido</TableHead>
                  <TableHead>Categoría</TableHead>
                  <TableHead className="text-right">Vistas</TableHead>
                  <TableHead className="text-right">Completados</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {c.top.map(t => (
                  <TableRow key={t.name}>
                    <TableCell className="max-w-[240px] truncate">{t.name}</TableCell>
                    <TableCell className="text-xs text-muted-foreground">{t.category}</TableCell>
                    <TableCell className="text-right">{t.vistas}</TableCell>
                    <TableCell className="text-right">{t.completados}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>

        <Card className="glass-card">
          <CardHeader><CardTitle className="text-base">Contenidos con baja finalización</CardTitle></CardHeader>
          <CardContent>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Contenido</TableHead>
                  <TableHead className="text-right">Vistas</TableHead>
                  <TableHead className="text-right">% Completado</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {c.low.map(t => (
                  <TableRow key={t.name}>
                    <TableCell className="max-w-[280px] truncate">{t.name}</TableCell>
                    <TableCell className="text-right">{t.vistas}</TableCell>
                    <TableCell className="text-right">{t.pct ?? 0}%</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
